import { Injectable } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { catchError, expand, map, reduce } from 'rxjs/operators';

import { ConfigurationService } from '@sondermind/configuration';
import { Logger } from '@sondermind/datadog-logging';
import { HttpService } from '@sondermind/http';
import {
  IAPIListResponse,
  IAPIListRequestOptions
} from '@sondermind/utilities/models-http';

import {
  ClientReferralLite,
  IClientReferralLiteApiResponse
} from '@sondermind/data-access/client-referral';

@Injectable({
  providedIn: 'root'
})
export class ClientPortalReferralsHttpService {
  constructor(
    private configurationService: ConfigurationService,
    private http: HttpService,
    private logger: Logger
  ) {}

  /**
   * Fetches all client portal referrals with automatic pagination
   * Gracefully handles pagination errors - returns partial data if later pages fail
   * @param opts Optional request options
   * @returns Observable of all ClientReferralLites across all pages (or partial if pagination fails)
   */
  fetchAll(opts: Partial<IAPIListRequestOptions> = {}): Observable<ClientReferralLite[]> {
    return this.fetchPage(opts).pipe(
      expand((response) => {
        if (!response.meta.links.next) {
          return EMPTY;
        }

        // Fetch next page with error handling to prevent pagination failures from breaking the entire stream
        return this.fetchPageByUrl(response.meta.links.next).pipe(
          catchError((error: unknown) => {
            this.logger.error('Pagination failed - returning data fetched so far', {
              failedUrl: response.meta.links.next,
              error
            });
            // Stop pagination by returning EMPTY, but allow the stream to complete with data collected so far
            return EMPTY;
          })
        );
      }),
      // Collect all pages and flatten the data arrays
      reduce((allReferrals: ClientReferralLite[], response) => allReferrals.concat(response.data), [])
    );
  }

  /**
   * Fetches a single page of client portal referrals
   * @param opts Optional request options
   * @returns Observable of IAPIListResponse with ClientReferralLites
   */
  fetchPage(opts: Partial<IAPIListRequestOptions> = {}): Observable<IAPIListResponse<ClientReferralLite>> {
    const params = this.http.generateListParams(opts);
    const url = `${this.configurationService.env.svcBase}/referrals/api/v1/client_referrals`;

    return this.http
      .get<IAPIListResponse<IClientReferralLiteApiResponse>>(url, params)
      .pipe(
        map((resp) => ({
          ...resp,
          data: resp.data.map((item) => new ClientReferralLite(item))
        })),
        this.http.handleHttpErrorStrict()
      );
  }

  /**
   * Fetches a page by direct URL (used for pagination)
   * @param url The full URL to fetch
   * @returns Observable of IAPIListResponse with ClientReferralLites
   */
  private fetchPageByUrl(url: string): Observable<IAPIListResponse<ClientReferralLite>> {
    return this.http
      .get<IAPIListResponse<IClientReferralLiteApiResponse>>(url)
      .pipe(
        map((resp) => ({
          ...resp,
          data: resp.data.map((item) => new ClientReferralLite(item))
        })),
        this.http.handleHttpErrorStrict()
      );
  }
}
